import { useResizeObserver, useWindowScroll } from '@vueuse/core'

/**
 * Header chrome: native sticky collapse via negative `top` offset equal to the
 * visible utility tier height. Utility stays in layout; scrolling slides it
 * off-screen while the primary bar sticks at the viewport top — no spacer,
 * no height toggle, no scrollHeight jitter.
 *
 * `frozen` / `setFrozen`: while true, treat as compact for a11y (utility inert)
 * and keep the sticky offset so mega-menu cannot re-expand the utility (R13/S40).
 * `--app-header-height` is always the header's viewport bottom (for overlay `top`),
 * not the compact row height — otherwise a frozen open at scrollY≈0 overlaps primary.
 *
 * `rootSelector` defaults to `.app-header` (class on UHeader sticky root).
 */
export function useHeaderChrome(rootSelector = '.app-header') {
  const { y: scrollY } = useWindowScroll()
  const mounted = ref(false)
  const frozen = ref(false)
  const headerRoot = ref<HTMLElement | null>(null)
  const utilityHeight = ref(0)

  const isCompact = computed(
    () =>
      frozen.value
      || (mounted.value && scrollY.value >= Math.max(utilityHeight.value, 1)),
  )
  const isExpanded = computed(() => !isCompact.value)

  function setFrozen(value: boolean) {
    frozen.value = value
  }

  function findVisibleUtility(rootEl: HTMLElement): HTMLElement | null {
    const candidates = rootEl.querySelectorAll<HTMLElement>(
      '.app-header-utility, .app-header-utility-mobile',
    )
    for (const el of candidates) {
      if (getComputedStyle(el).display !== 'none') {
        return el
      }
    }
    return null
  }

  function syncHeaderHeights() {
    const rootEl = headerRoot.value
    if (!rootEl) {
      return
    }

    const utilEl = findVisibleUtility(rootEl)
    const rootRect = rootEl.getBoundingClientRect()
    const uHeight = utilEl
      ? Math.round(utilEl.getBoundingClientRect().height)
      : 0
    const tHeight = Math.round(rootRect.height)
    const cHeight = Math.max(0, tHeight - uHeight)
    // Viewport Y of header bottom — overlays (mega-menu) sit flush under chrome.
    // Do not use compact height while utility is still on-screen (scrollY≈0 + frozen).
    const headerBottom = Math.max(0, Math.round(rootRect.bottom))

    utilityHeight.value = uHeight

    // Sticky offset: utility scrolls off-screen; primary sticks at viewport top.
    rootEl.style.top = uHeight > 0 ? `-${uHeight}px` : '0px'
    rootEl.style.setProperty('--app-header-utility-height', `${uHeight}px`)
    rootEl.style.setProperty('--app-header-height', `${headerBottom}px`)
    rootEl.style.setProperty('--app-header-height-compact', `${cHeight}px`)

    document.documentElement.style.setProperty(
      '--app-header-utility-height',
      `${uHeight}px`,
    )
    document.documentElement.style.setProperty(
      '--app-header-height',
      `${headerBottom}px`,
    )
    document.documentElement.style.setProperty(
      '--app-header-height-compact',
      `${cHeight}px`,
    )
  }

  onMounted(() => {
    headerRoot.value = document.querySelector<HTMLElement>(rootSelector)
    syncHeaderHeights()
    mounted.value = true
    // Re-measure after layout (fonts / responsive utility swap).
    nextTick(() => syncHeaderHeights())
  })

  useResizeObserver(headerRoot, () => {
    syncHeaderHeights()
  })

  watch([scrollY, isCompact, frozen], () => {
    syncHeaderHeights()
  })

  return {
    scrollY,
    mounted,
    frozen,
    setFrozen,
    isCompact,
    isExpanded,
    utilityHeight,
  }
}
